import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { setCachedItComponentsCount } from 'composables/useItComponentsCountCache.js'

const PAGE_LIMIT = 30

const SORT_PREFERENCE_KEY = 'it_components_sort_preference'
const DEFAULT_SORT = { by: 'created_at', order: 'desc' }

// The user's last chosen sort is a standing preference, not per-organization
// state — read once at store creation (so it's already correct on the very
// first render) and written back on every setSort. Falls back to
// DEFAULT_SORT for a first-ever visit, a cleared/blocked localStorage, or a
// stored value that no longer matches "by-order" (e.g. an old SORT_OPTIONS
// value from before a future rename).
function loadStoredSort() {
  try {
    const raw = localStorage.getItem(SORT_PREFERENCE_KEY)
    if (!raw) return { ...DEFAULT_SORT }

    const [by, order] = raw.split('-')
    if (!by || !['asc', 'desc'].includes(order)) return { ...DEFAULT_SORT }
    return { by, order }
  } catch {
    return { ...DEFAULT_SORT }
  }
}

function storeSortPreference(by, order) {
  try {
    localStorage.setItem(SORT_PREFERENCE_KEY, `${by}-${order}`)
  } catch {
    // Storage unavailable (private browsing, disabled site data, etc.) — the
    // choice just won't persist to the next visit, same as today.
  }
}

// GET requests go through axios.get(url, { headers }) — query params must be
// embedded in the URL itself, they aren't sent from a request body.
function buildQuery(filters, sort, page) {
  const parts = [`page[number]=${page}`, `page[limit]=${PAGE_LIMIT}`, `sort_by=${sort.by}`, `sort_order=${sort.order}`]
  if (filters.search) parts.push(`search=${encodeURIComponent(filters.search)}`)
  filters.type.forEach((value) => parts.push(`type[]=${encodeURIComponent(value)}`))
  return parts.join('&')
}

export const useOrgComponentsStore = defineStore('orgComponents', {
  state: () => ({
    organizationId: null,
    components: [],
    pageInfo: { current_page: 0, total_pages: 0, total_records: 0 },
    filters: { search: '', type: [] },
    sort: loadStoredSort(),
    loading: false,
    loadingMore: false,
    error: null,
    fetchedAt: null,
  }),

  getters: {
    hasMore: (state) => state.pageInfo.current_page === 0 || state.pageInfo.current_page < state.pageInfo.total_pages,
    hasActiveFilters: (state) => !!state.filters.search || state.filters.type.length > 0,
  },

  actions: {
    async init(organizationId) {
      if (this.organizationId === organizationId && this.fetchedAt) return
      this.organizationId = organizationId
      this.filters = { search: '', type: [] }
      // Sort is intentionally left alone here — it's a standing user
      // preference (see loadStoredSort), not per-organization state, so
      // switching organizations shouldn't reset it back to the default.
      await this.fetchPage(true)
      // Only cache from this true, unfiltered initial load — fetchPage() also
      // runs for search/filter/sort changes, whose totals aren't what the
      // next visit's skeleton should show.
      if (!this.error) setCachedItComponentsCount(organizationId, this.pageInfo.total_records)
    },

    async setSort(by, order) {
      this.sort = { by, order }
      storeSortPreference(by, order)
      await this.fetchPage(true)
    },

    async setSearch(value) {
      this.filters.search = value
      await this.fetchPage(true)
    },

    async toggleType(value) {
      const idx = this.filters.type.indexOf(value)
      if (idx === -1) this.filters.type.push(value)
      else this.filters.type.splice(idx, 1)
      await this.fetchPage(true)
    },

    async clearFilters() {
      this.filters = { search: '', type: [] }
      await this.fetchPage(true)
    },

    // Distinct from clearFilters() — leaves an active search term in place,
    // for the category dropdown's own "Clear" action (which should only
    // touch the categories, not whatever the user separately typed in).
    async clearTypeFilters() {
      this.filters.type = []
      await this.fetchPage(true)
    },

    async fetchMore() {
      if (this.loadingMore || this.loading || !this.hasMore) return
      await this.fetchPage(false)
    },

    async fetchPage(reset) {
      if (reset) {
        this.loading = true
        this.error = null
      } else {
        this.loadingMore = true
      }

      const nextPage = reset ? 1 : this.pageInfo.current_page + 1
      const query = buildQuery(this.filters, this.sort, nextPage)

      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/organizations/${this.organizationId}/org_components?${query}`, {}, false)
        if (!res.js_error && res.response.success) {
          const { records, page } = res.response.data
          this.components = reset ? records : [...this.components, ...records]
          this.pageInfo = page
          this.fetchedAt = Date.now()
        } else {
          this.error = 'Failed to load IT components.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    async addComponent(payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', `/api/v1/organizations/${this.organizationId}/org_components`, { org_component: payload }, false)
      if (!res.js_error && res.response.success) {
        await this.fetchPage(true)
      }
      return res
    },

    // Optimistic add for the catalog drag/click-add flow: the item appears in
    // `components` immediately (so the UI moves it left→right in the same
    // frame as the user's action, no waiting on the network), then either
    // gets reconciled with the real record or rolled back on failure.
    //
    // `_key` stays the same (the temp id) across that reconciliation even
    // though `id` changes from the temp value to the real one — the template
    // renders TransitionGroup items keyed on `_key`, not `id`, specifically so
    // swapping in the real record doesn't look like the temp one left and a
    // brand new one arrived (which replayed the enter animation a second time).
    async addComponentOptimistic(globalComponent) {
      const tempId = `optimistic-${globalComponent.id}`
      this.components = [{
        id: tempId,
        _key: tempId,
        name: globalComponent.name,
        vendor: globalComponent.vendor,
        type: globalComponent.type,
        logo_url: globalComponent.logo_url,
        created_at: new Date().toISOString(),
      }, ...this.components]
      this.pageInfo.total_records += 1

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', `/api/v1/organizations/${this.organizationId}/org_components`, { org_component: { name: globalComponent.name } }, false)

      if (!res.js_error && res.response.success) {
        const idx = this.components.findIndex((c) => c.id === tempId)
        if (idx !== -1) this.components[idx] = { ...res.response.data, _key: tempId }
      } else {
        this.components = this.components.filter((c) => c.id !== tempId)
        this.pageInfo.total_records = Math.max(0, this.pageInfo.total_records - 1)
      }

      return res
    },

    async updateComponent(componentId, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/organizations/${this.organizationId}/org_components/${componentId}`, { org_component: payload }, false)
      if (!res.js_error && res.response.success) {
        const component = this.components.find((c) => c.id === componentId)
        if (component) Object.assign(component, res.response.data)
      }
      return res
    },

    async deleteComponent(componentId) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/organizations/${this.organizationId}/org_components/${componentId}`, {}, false)
      if (!res.js_error && res.response.success) {
        this.components = this.components.filter((c) => c.id !== componentId)
        this.pageInfo.total_records = Math.max(0, this.pageInfo.total_records - 1)
      }
      return res
    },

    // Mirror of addComponentOptimistic for the drag-to-catalog removal flow:
    // gone from the list immediately, restored at its original index if the
    // DELETE actually fails.
    async removeComponentOptimistic(componentId) {
      const idx = this.components.findIndex((c) => c.id === componentId)
      if (idx === -1) return { js_error: false, response: { success: true } }
      const removed = this.components[idx]

      this.components = this.components.filter((c) => c.id !== componentId)
      this.pageInfo.total_records = Math.max(0, this.pageInfo.total_records - 1)

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/organizations/${this.organizationId}/org_components/${componentId}`, {}, false)

      if (res.js_error || !res.response.success) {
        const restored = [...this.components]
        restored.splice(idx, 0, removed)
        this.components = restored
        this.pageInfo.total_records += 1
      }

      return res
    },
  },
})
